import React, { useMemo } from 'react';
import type ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import type {
  VisibilityReportPeriodType,
  VisibilityReportTrendPoint,
} from '@brand24-original/llm-types';
import ReportSection from './ReportSection';
import { prepareHeaderDate } from '../../helpers/filters/prepareHeaderDate';
import { PeriodType } from '../../types/prompt/PeriodType';
import BrandPositionChart from '../../../pages/projects/preview/components/brand-position-chart/BrandPositionChart';
import './ReportPerformanceTrend.scss';

/** `null` w macierzy oznacza „nie zmierzono" — ECharts rysuje przerwę zamiast punktu w zerze. */
const nullValue = null as unknown as string | number;

interface ReportPerformanceTrendProps {
  trend: VisibilityReportTrendPoint[];
  /** Gradacja raportu — decyduje o podpisach osi (przy `WEEKLY` punkt to zakres, nie jeden dzień). */
  periodType?: VisibilityReportPeriodType;
  /** Raport zrzuca z tej instancji obraz wykresu do PDF-a. */
  chartRef?: React.RefObject<ReactECharts>;
}

/**
 * Ten sam `BrandPositionChart`, co na podglądzie projektu — raport i panel nie rozjadą się przy
 * kolejnej zmianie wykresu. Karmimy go w `percentageMode` jedną macierzą procentową (wynik marki +
 * widoczność) i pozycją jako serią pomocniczą. `monitorHeaders` to legenda i musi odpowiadać
 * kolumnom macierzy, inaczej podpisze widoczność jako pozycję.
 */
export const ReportPerformanceTrend: React.FC<ReportPerformanceTrendProps> = ({
  trend,
  periodType = 'DAILY',
  chartRef,
}) => {
  const { t } = useTranslation();
  // Ten sam podpis, co na V2 (`createProjectStatsMatrix`): przy WEEKLY punkt opisuje CAŁY tydzień,
  // więc oś pokazuje zakres, a nie sam poniedziałek.
  const axisLabel = (date: string) => prepareHeaderDate(date, periodType as PeriodType);

  const percentageMatrix = useMemo<(string | number)[][]>(
    () => [
      ['date', t('brandScore'), t('visibility')],
      ...trend.map((point) => [
        axisLabel(point.date),
        point.brandScore ?? nullValue,
        point.visibility ?? nullValue,
      ]),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trend, t, periodType]
  );

  const positionMatrix = useMemo<(string | number)[][]>(
    () => [
      ['date', t('averagePosition')],
      ...trend.map((point) => [axisLabel(point.date), point.averagePosition ?? nullValue]),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trend, t, periodType]
  );

  const monitorHeaders = useMemo(() => [t('brandScore'), t('visibility')], [t]);

  // Slajd bez danych w ogóle nie wchodzi do raportu — zakres może nie mieć jeszcze żadnego dnia.
  if (trend.length === 0) {
    return null;
  }

  return (
    <ReportSection
      title={t('reportTrendTitle')}
      titleAccent={t('reportTrendVisibilityAccent')}
      subtitle={t('reportTrendSubtitle')}
    >
      {/* `--full-width`, bo w panelu obok wykresu stoi jeszcze Share of Voice i siatka rezerwuje na
          niego 360px — w raporcie ta kolumna jest pusta. */}
      <div className="stats stats--full-width report-trend">
        <BrandPositionChart
          chartRef={chartRef}
          averagePositionData={percentageMatrix}
          averagePositionSecondaryData={positionMatrix}
          brandScoreData={[]}
          percentageMode={true}
          // Raport pokazuje od razu wszystkie trzy serie — w panelu średnią pozycję odsłania się
          // kliknięciem w legendę, ale slajdu nikt nie klika.
          hideAveragePositionInitially={false}
          monitorHeaders={monitorHeaders}
          period={periodType as PeriodType}
          eventsData={null}
          hasAnyPendingQuestions={false}
          hasProjectStatsData
          isLoading={false}
          skipPanelSettings
        />
      </div>
    </ReportSection>
  );
};

export default ReportPerformanceTrend;
